/*
 * Обводки аватара гильдии и кружок-заглушка.
 *
 * Готовых гербов игра не предлагает: игрок грузит свою картинку либо
 * пропускает шаг, и тогда рисуется первая буква названия.
 *
 * Два вида обводок (data/frames.csv, колонка kind):
 *   vector — металлическое кольцо рисуется SVG. Векторный круг сглаживается
 *            на любой плотности экрана.
 *   art    — рисованная картинка /art/frames/<art>-<размер>.webp поверх
 *            аватара. Отверстие в картинке — ровно ART_HOLE её ширины,
 *            по центру; это проверяется замером при подготовке файлов
 *            (apps/client/public/art/SOURCES.md).
 */

export interface FrameVisual {
  /** Металл кольца: светлый край, основной тон, тёмный край. */
  readonly metal: readonly [string, string, string];
  /** Толщина кольца в долях размера аватара. */
  readonly weight: number;
  /**
   * Перелив по кольцу.
   *   none   — металл стоит
   *   glint  — узкий блик обегает кольцо
   *   prism  — радужный перелив вращается по всему кольцу
   */
  readonly shimmer: 'none' | 'glint' | 'prism';
  /** Цвета перелива для prism: точка градиента 0..1 и цвет. */
  readonly prism?: readonly (readonly [number, string])[];
}

export const FRAME_VISUALS: Readonly<Record<string, FrameVisual>> = {
  // Простая — светлая сталь, а не тёмно-серая: прежняя сливалась с фоном (1.5:1).
  frame_basic: { metal: ['#c9ced8', '#8a909c', '#4d525c'], weight: 0.035, shimmer: 'none' },
  frame_bronze: { metal: ['#f0c08a', '#b07440', '#5e3818'], weight: 0.045, shimmer: 'glint' },
  frame_silver: { metal: ['#ffffff', '#b8c0cc', '#5c6470'], weight: 0.045, shimmer: 'glint' },
  frame_gold: { metal: ['#fff0b8', '#d9a83a', '#7a5410'], weight: 0.055, shimmer: 'glint' },
  frame_arcane: {
    metal: ['#d8dcff', '#6f7ae0', '#2a2a6e'],
    weight: 0.06,
    shimmer: 'prism',
    prism: [
      [0, '#7f8cff'],
      [0.25, '#b48cff'],
      [0.5, '#6fd6ff'],
      [0.75, '#9ff0d8'],
      [1, '#7f8cff'],
    ],
  },
};

export const FALLBACK_FRAME: FrameVisual = FRAME_VISUALS.frame_basic ?? {
  metal: ['#c9ced8', '#8a909c', '#4d525c'],
  weight: 0.035,
  shimmer: 'none',
};

/** Доля ширины рисованной обводки, занятая отверстием под аватар. */
export const ART_HOLE = 0.5;

/** Размеры файлов рисованных обводок, px. */
const ART_SIZES = [192, 384] as const;

/**
 * Файл рисованной обводки под размер аватара на экране.
 *
 * Берётся наименьший файл не мельче нужного (ширина обводки × плотность),
 * иначе картинка мылится. Выбор в JS, а не через srcset: обводка рисуется
 * внутри SVG (<image>), а у него srcset нет.
 */
export function frameArtUrl(art: string, avatarCssSize: number, dpr: number): string {
  const need = (avatarCssSize / ART_HOLE) * dpr;
  const size = ART_SIZES.find((s) => s >= need) ?? ART_SIZES[ART_SIZES.length - 1];
  return `/art/frames/${art}-${size}.webp`;
}

/*
 * Цвета кружка без логотипа.
 *
 * Каждый затемнён так, чтобы белая буква давала не меньше 4.6:1 (WCAG AA).
 * Прежний набор проходил порог только у двух цветов из восьми.
 */
const LETTER_COLORS = [
  '#5e6ad2',
  '#2f78bf',
  '#30816a',
  '#976b3f',
  '#c1504b',
  '#9758c0',
  '#498149',
  '#986c30',
] as const;

/** Цвет выводится из названия: одна гильдия выглядит одинаково у всех. */
export function colorForName(name: string): string {
  let hash = 0;
  for (const ch of name.trim().toLowerCase()) {
    hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) % 100_000;
  }
  return LETTER_COLORS[hash % LETTER_COLORS.length] ?? LETTER_COLORS[0];
}

/** Первая буква названия для кружка-заглушки. */
export function initialOf(name: string): string {
  const first = Array.from(name.trim())[0];
  return first === undefined ? '?' : first.toUpperCase();
}
